/**
 * LA PROCHAINE ACTION UTILE — Bricolo permanent (ROADMAP 3.5), **logique pure**.
 *
 * ══ Ce que ce module rend mécanique ══════════════════════════════════════════
 *
 * Le §1 pose une règle de vérification : « ouvrir le jeu à n'importe quel moment de la
 * progression et se demander *qu'est-ce que je vise, là ?*. Si la réponse est *rien*,
 * c'est un défaut bloquant. »
 *
 * Cette question se posait à la main, une fois de temps en temps, par un humain de bonne
 * volonté. Elle se pose désormais **à chaque tick**, et un conseil qui manquerait se voit
 * dans une sonde au lieu de se découvrir dans un avis de joueur.
 *
 * Bricolo n'existait que dans le tutoriel (`ui/tutorial.ts`) et disparaissait ensuite —
 * exactement au moment où le joueur commence à avoir besoin qu'on lui dise où regarder.
 *
 * ══ CALCULÉ, JAMAIS SCRIPTÉ ══════════════════════════════════════════════════
 *
 * Aucune séquence, aucune liste d'étapes : chaque conseil est une **question posée à
 * l'état**, et le premier qui répond « moi » gagne. Un script dirait la même chose à un
 * joueur de la première heure et à un joueur de la cinquième ère ; une question, non.
 *
 * ⚠️ **Ce module ne connaît AUCUN texte** (règle d'architecture §4). Il rend une sorte et
 * des nombres ; `src/i18n` en fait une phrase. C'est ce qui permet de le tester sans
 * navigateur et de le traduire sans le toucher.
 *
 * ══ L'ORDRE EST UNE DÉCISION, PAS UN HASARD ══════════════════════════════════
 *
 * Du plus PÉRISSABLE au plus permanent : ce qui se perd si on ne le fait pas maintenant
 * passe avant ce qui attendra toujours. Une quête encaissable expire à minuit ; poser un
 * bâtiment sera encore possible demain.
 */

import { nextCost, nextLandCost, unlockProgress, unlockedTypes } from './economy';
import {
  activeIsland,
  buildingCounts,
  currentProduction,
  prestigeProgress,
  questCounters,
} from './gameState';
import type { CitySnapshot } from './gameState';
import { prestigePreview } from './prestige';
import { questProgress } from './quests';
import { orphanRoadCount } from './roadCleanup';
import { ECONOMY } from '@/config/balance';
import { BUILDINGS } from '@/config/buildings';

/**
 * Sortes de conseils. Chaque sorte correspond à UNE question posée à l'état, et à une
 * clé de traduction du même nom côté interface.
 */
export type AdviceKind =
  /** Une quête est terminée et attend son encaissement. */
  | 'quest'
  /** Une nouvelle ère est ouverte. */
  | 'era'
  /** Une décoration payée dort au sac à dos. */
  | 'bag'
  /** Du bitume ne dessert plus rien : du terrain à récupérer. */
  | 'roads'
  /** Le terrain est plein et une extension est payable. */
  | 'land'
  /** Un bâtiment est payable tout de suite. */
  | 'build'
  /** Il manque N exemplaires pour ouvrir le type suivant. */
  | 'unlock'
  /** Rien d'urgent : la ville produit, on peut la montrer. */
  | 'share';

export interface Advice {
  readonly kind: AdviceKind;
  /**
   * Quantité qui donne son sens au conseil — exemplaires manquants, cases à rendre,
   * objets au sac. Zéro quand la sorte n'en a pas besoin.
   */
  readonly value: number;
}

/**
 * Le conseil du moment, ou `null` si la ville n'existe pas encore.
 *
 * ⚠️ **Rend `null` le moins souvent possible, et c'est le contrat.** Un `null` signifie
 * « je ne vois rien à viser », c'est-à-dire précisément l'état que le §1 interdit. La
 * sonde de ce module vérifie qu'il ne se produit à aucun des moments d'une partie
 * simulée.
 *
 * @param exclude sorte déjà dite juste avant — Bricolo ne se répète pas deux fois de
 *                suite, sans quoi il devient un bandeau et non un compagnon.
 */
export function nextAdvice(
  snapshot: CitySnapshot,
  now: number,
  exclude?: AdviceKind,
): Advice | null {
  const candidates: Advice[] = [];

  /* ── Périssable : ce qui se perd si on ne le fait pas ────────────────────── */

  const claimable = questProgress(snapshot.quests, questCounters(snapshot)).filter(
    (entry) => entry.complete && !entry.claimed,
  ).length;
  if (claimable > 0) candidates.push({ kind: 'quest', value: claimable });

  if (prestigePreview(prestigeProgress(snapshot), snapshot.prestigeMultiplier).available) {
    candidates.push({ kind: 'era', value: 0 });
  }

  /* ── Payé et pas encore utilisé : rien ne se perd, mais rien ne sert ─────── */

  if (snapshot.backpack.length > 0) {
    candidates.push({ kind: 'bag', value: snapshot.backpack.length });
  }

  const orphans = orphanRoadCount(snapshot, now);
  if (orphans > 0) candidates.push({ kind: 'roads', value: orphans });

  /* ── Construire : le cœur de la boucle ───────────────────────────────────── */

  const counts = buildingCounts(snapshot);
  const open = unlockedTypes(counts, snapshot.prestigeLevel);
  const affordable = open.filter((type) => {
    const definition = BUILDINGS.find((entry) => entry.type === type);
    if (definition === undefined) return false;
    const owned = counts[type] ?? 0;
    // Le prix COURANT du type, par la même fonction que la boutique : recopier la
    // formule ici la ferait diverger au premier ajustement (règle 22).
    return snapshot.coins >= nextCost(definition, owned);
  });
  if (affordable.length > 0) candidates.push({ kind: 'build', value: affordable.length });

  const landLeft = snapshot.landPurchases < ECONOMY.land.maxPurchases;
  if (landLeft && snapshot.coins >= nextLandCost(snapshot.landPurchases)) {
    candidates.push({ kind: 'land', value: 0 });
  }

  /*
    Le prochain type à ouvrir. C'est le conseil de repli le plus solide du jeu : tant
    que le catalogue n'est pas épuisé, il reste TOUJOURS un « encore N maisons ».
  */
  const sealed = BUILDINGS.find((definition) => !open.includes(definition.type));
  if (sealed !== undefined) {
    const gate = unlockProgress(counts, sealed.type, snapshot.prestigeLevel);
    if (gate !== null) candidates.push({ kind: 'unlock', value: gate.need - gate.owned });
  }

  /*
    Dernier recours, et il n'est pas décoratif : une ville qui produit se MONTRE, et
    chaque visite unique rend un boost (§9). Quand tout le reste est fait, c'est
    littéralement la seule chose qui accélère encore la partie.
  */
  if (currentProduction(snapshot) > 0) candidates.push({ kind: 'share', value: 0 });

  const fresh = candidates.filter((advice) => advice.kind !== exclude);
  /*
    Si le seul conseil disponible est celui qu'on vient de dire, on le REDIT plutôt que
    de se taire : « ne pas se répéter » est une règle de confort, « toujours avoir un
    objectif » est le §1. La seconde gagne.
  */
  return fresh[0] ?? candidates[0] ?? null;
}

/** Nombre de cases libres — utile aux appelants qui veulent nuancer le conseil. */
export function freeCellCount(snapshot: CitySnapshot): number {
  const island = activeIsland(snapshot);
  return island.grid.cols * island.grid.rows - island.roads.length;
}
